import React, { useEffect, useState, Children } from 'react';
import { Users, MapPin, ChevronRight, Building2, UserPlus } from 'lucide-react';
import { Card } from '../ui/Card';
import { Candidate, Branch } from '../../types/hr.types';
import { mockCandidates } from '../../utils/mockData';
import { isToday, isThisWeek, isThisMonth, formatDate } from '../../utils/dateUtils';
import { motion, AnimatePresence } from 'framer-motion';
interface RecruitingWidgetProps {
  onCandidateClick: (candidate: Candidate) => void;
  candidates?: Candidate[];
}
type Period = 'today' | 'week' | 'month';
export function RecruitingWidget({
  onCandidateClick,
  candidates: externalCandidates
}: RecruitingWidgetProps) {
  const [period, setPeriod] = useState<Period>('today');
  const [filteredCandidates, setFilteredCandidates] = useState<Candidate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const sourceCandidates = externalCandidates || mockCandidates;
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      const filtered = sourceCandidates.filter(c => {
        if (period === 'today') return isToday(c.phoneInterviewDate);
        if (period === 'week') return isThisWeek(c.phoneInterviewDate);
        if (period === 'month') return isThisMonth(c.phoneInterviewDate);
        return false;
      });
      setFilteredCandidates(filtered);
      setIsLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [period, sourceCandidates]);
  const groupedCandidates = {
    Troy: filteredCandidates.filter(c => c.branch === 'Troy'),
    Arizona: filteredCandidates.filter(c => c.branch === 'Arizona'),
    Miami: filteredCandidates.filter(c => c.branch === 'Miami')
  };
  const branches: {
    name: Branch;
    gradient: string;
    iconColor: string;
    bg: string;
  }[] = [{
    name: 'Troy',
    gradient: 'from-blue-500 to-blue-600',
    iconColor: 'text-blue-600',
    bg: 'bg-blue-50/50'
  }, {
    name: 'Arizona',
    gradient: 'from-amber-500 to-orange-500',
    iconColor: 'text-orange-600',
    bg: 'bg-orange-50/50'
  }, {
    name: 'Miami',
    gradient: 'from-teal-400 to-cyan-500',
    iconColor: 'text-teal-600',
    bg: 'bg-teal-50/50'
  }];
  const getStatusColor = (status: Candidate['status']) => {
    switch (status) {
      case 'Ready to Start':
        return 'bg-green-500';
      case 'Pre-boarding':
        return 'bg-amber-500';
      case 'Offer Signed':
        return 'bg-blue-500';
      case 'Phone Screen':
        return 'bg-purple-500';
      default:
        return 'bg-gray-400';
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  const CandidateItem = ({
    candidate
  }: {
    candidate: Candidate;
  }) => <motion.button variants={itemVariants} onClick={() => onCandidateClick(candidate)} className="w-full text-left group relative flex items-center justify-between p-3 rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-orange-100 transition-all duration-200 overflow-hidden" whileHover={{
    scale: 1.01,
    y: -1
  }} whileTap={{
    scale: 0.99
  }} data-id="element-1096">
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${getStatusColor(candidate.status)}`} data-id="element-1097" />
      <div className="flex items-center gap-3 min-w-0 pl-2" data-id="element-1098">
        <div className="h-9 w-9 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-orange-50 group-hover:text-orange-500 transition-colors flex-shrink-0 border border-gray-100" data-id="element-1099">
          <UserPlus className="h-4.5 w-4.5" data-id="element-1100" />
        </div>
        <div className="min-w-0" data-id="element-1101">
          <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-orange-700 transition-colors" data-id="element-1102">
            {candidate.firstName} {candidate.lastName}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-1103">{candidate.role}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 pl-2" data-id="element-1104">
        <div className="text-right hidden sm:block" data-id="element-1105">
          <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400 block" data-id="element-1106">
            Interview
          </span>
          <span className="text-xs font-medium text-gray-700" data-id="element-1107">
            {formatDate(candidate.phoneInterviewDate, {
            month: 'short',
            day: 'numeric'
          })}
          </span>
        </div>
        <div className="h-6 w-6 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-orange-100 transition-colors" data-id="element-1108">
          <ChevronRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-orange-600 transition-colors" data-id="element-1109" />
        </div>
      </div>
    </motion.button>;
  return <Card title="Recruiting Overview" icon={<Users className="h-5 w-5 text-orange-500" data-id="element-1111" />} className="h-full flex flex-col" data-id="element-1110">
      <div className="relative flex p-1 bg-gray-100/80 rounded-xl mb-6 backdrop-blur-sm" data-id="element-1112">
        {(['today', 'week', 'month'] as Period[]).map(p => <button key={p} onClick={() => setPeriod(p)} className={`relative flex-1 py-2 text-xs font-semibold rounded-lg transition-colors z-10 ${period === p ? 'text-orange-700' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1113">
            {period === p && <motion.div layoutId="activeTab" className="absolute inset-0 bg-white rounded-lg shadow-sm border border-gray-200/50" transition={{
          type: 'spring',
          bounce: 0.2,
          duration: 0.6
        }} data-id="element-1114" />}
            <span className="relative z-10 capitalize" data-id="element-1115">
              {p === 'today' ? 'Today' : p === 'week' ? 'This Week' : 'This Month'}
            </span>
          </button>)}
      </div>
      {isLoading ? <div className="space-y-8 animate-pulse px-1" data-id="element-1116">
          {[1, 2, 3].map(i => <div key={i} className="space-y-3" data-id="element-1117">
              <div className="flex items-center gap-2" data-id="element-1118">
                <div className="h-5 w-5 rounded bg-gray-200" data-id="element-1119" />
                <div className="h-5 w-24 bg-gray-200 rounded" data-id="element-1120" />
              </div>
              <div className="h-16 bg-gray-100 rounded-xl" data-id="element-1121" />
              <div className="h-16 bg-gray-100 rounded-xl" data-id="element-1122" />
            </div>)}
        </div> : <div className="space-y-8 overflow-y-auto max-h-[calc(100vh-300px)] pr-2 -mr-2 custom-scrollbar pb-4" data-id="element-1123">
          <AnimatePresence mode="wait" data-id="element-1124">
            <motion.div key={period} variants={containerVariants} initial="hidden" animate="show" className="space-y-8" data-id="element-1125">
              {branches.map(branch => {
            const branchCandidates = groupedCandidates[branch.name];
            return <motion.div key={branch.name} variants={itemVariants} className="space-y-3" data-id="element-1126">
                    <div className="flex items-center gap-2 pb-2 border-b border-gray-100" data-id="element-1127">
                      <div className={`p-1.5 rounded-md bg-gradient-to-br ${branch.gradient} shadow-sm`} data-id="element-1128">
                        <MapPin className="h-3.5 w-3.5 text-white" data-id="element-1129" />
                      </div>
                      <h3 className="text-sm font-bold text-gray-900" data-id="element-1130">
                        {branch.name}
                      </h3>
                      <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200" data-id="element-1131">
                        {branchCandidates.length}
                      </span>
                    </div>
                    {branchCandidates.length > 0 ? <div className="space-y-2" data-id="element-1132">
                        {branchCandidates.map(candidate => <CandidateItem key={candidate.id} candidate={candidate} data-id="element-1133" />)}
                      </div> : <div className={`py-6 px-4 rounded-xl border border-dashed border-gray-200 ${branch.bg} flex flex-col items-center justify-center text-center`} data-id="element-1134">
                        <Building2 className={`h-8 w-8 mb-2 opacity-20 ${branch.iconColor}`} data-id="element-1135" />
                        <p className="text-xs font-medium text-gray-500" data-id="element-1136">
                          No interviews in {branch.name}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-0.5" data-id="element-1137">
                          {period === 'today' ? 'for today' : period === 'week' ? 'this week' : 'this month'}
                        </p>
                      </div>}
                  </motion.div>;
          })}
            </motion.div>
          </AnimatePresence>
        </div>}
    </Card>;
}